<template>
    <div>
        <div class="d-flex align-items-baseline mb-50">
            <h5 class="mb-0">{{ $t('v4.Requests') }}</h5>
            <span class="ml-1 small text-muted">
                {{ $t('v4.Shown for 24 hours after completion') }}
            </span>
        </div>

        <b-card
            v-if="!jobs.length"
            no-body
            class="p-2 text-center text-muted"
        >
            {{ $t('org.message.No Data') }}
        </b-card>

        <b-card
            v-for="job in jobs"
            :key="job.jobId"
            no-body
            class="mb-1"
        >
            <div class="p-1 border-bottom d-flex align-items-start">
                <div class="flex-grow-1">
                    <div class="font-weight-bold">
                        {{ groupLabel(job) }}
                    </div>
                    <div class="small text-muted">
                        {{ job.startMonth }} ~ {{ job.endMonth }}
                        · {{ $t('v4.{count} months', { count: job.totalMonths }) }}
                    </div>
                    <div class="small text-muted">
                        {{ statusText(job) }}
                    </div>
                    <div class="small text-muted mt-25">
                        {{ $t('v4.Recipients') }}
                        <b>{{ maskedRecipients(job) }}</b>
                        <span v-if="job.expireAt">
                            · {{ $t('v4.Expires at {time}', { time: job.expireAt }) }}
                        </span>
                    </div>
                </div>
                <b-badge :variant="badgeVariant(job.status)">
                    {{ $t(`v4.bulkStatus.${job.status}`) }}
                </b-badge>
            </div>

            <div
                v-for="month in job.months"
                :key="`${job.jobId}-${month.month}`"
                class="px-1 py-50 border-bottom d-flex align-items-center small"
            >
                <span class="flex-grow-1">
                    {{ month.month }}
                    <span
                        v-if="month.downloadedCount"
                        class="text-muted"
                    >
                        · {{ $t('v4.{count} files', { count: month.downloadedCount }) }}
                    </span>
                    <!--
                        생성 시각과 크기. 이용내역서 데이터는 월마감 과정에서 바뀌므로
                        "언제 만든 파일인지" 가 최신 여부를 판단할 유일한 근거다(BO-983).
                    -->
                    <span
                        v-if="month.completedAt"
                        class="text-muted"
                    >
                        · {{ $t('v4.Created at {time}', { time: shortTime(month.completedAt) }) }}
                    </span>
                    <span
                        v-if="month.zipBytes"
                        class="text-muted"
                    >
                        · {{ formatBytes(month.zipBytes) }}
                    </span>

                    <b-badge
                        v-if="month.unissuedAccounts && month.unissuedAccounts.length"
                        variant="light-secondary"
                        class="ml-50"
                        :title="month.unissuedAccounts.join(', ')"
                    >
                        {{ $t('v4.Not issued') }} {{ month.unissuedAccounts.length }}
                    </b-badge>
                    <b-badge
                        v-if="month.failedAccounts && month.failedAccounts.length"
                        variant="light-danger"
                        class="ml-25"
                        :title="month.failedAccounts.join(', ')"
                    >
                        {{ $t('v4.Failed') }} {{ month.failedAccounts.length }}
                    </b-badge>

                    <!--
                        메일 발송 결과. 용량이 커서 첨부하지 못한 경우를 알려주지 않으면
                        "왜 메일에 파일이 없나" 를 사용자가 알 방법이 없다.
                    -->
                    <b-badge
                        v-if="month.mailResult === 'ATTACHED'"
                        variant="light-success"
                        class="ml-25"
                    >
                        {{ $t('v4.Sent as email attachment') }}
                    </b-badge>
                    <!--
                        지난 발송이 용량으로 실패했더라도 지금 보낼 수 있으면 감춘다.
                        이 배지의 목적은 "왜 메일에 파일이 없나" 를 설명하는 것인데,
                        한도가 올라가 지금은 보낼 수 있다면 그 설명이 더는 맞지 않는다.
                        배지(지난 결과)와 버튼(지금 가능 여부)이 반대말로 나란히 서면
                        읽는 사람이 어느 쪽을 믿어야 할지 알 수 없다(BO-983).
                    -->
                    <b-badge
                        v-else-if="month.mailResult === 'TOO_LARGE' && !canMail(month)"
                        variant="light-warning"
                        class="ml-25"
                    >
                        {{ $t('v4.Too large for email - download here') }}
                    </b-badge>
                    <b-badge
                        v-else-if="month.mailResult === 'FAILED'"
                        variant="light-danger"
                        class="ml-25"
                    >
                        {{ $t('v4.Email delivery failed') }}
                    </b-badge>
                </span>

                <!--
                    같은 조건은 목록에 한 줄이므로(서버가 최신 1건만 준다) "다시 만들기" 를
                    눌러도 줄이 늘지 않고 그 자리가 갱신된다. 기존 요청 API 를 그대로 쓴다.
                -->
                <b-button
                    v-if="month.downloadable"
                    size="sm"
                    variant="outline-secondary"
                    class="text-nowrap mr-50"
                    :disabled="regenerating === job.jobId"
                    @click="regenerate(job)"
                >
                    <b-spinner
                        v-if="regenerating === job.jobId"
                        small
                        class="mr-25"
                    />
                    {{ $t('v4.Regenerate') }}
                </b-button>
                <!--
                    메일 발송은 요청 시점이 아니라 여기서 고른다 — 실제 크기를 보고
                    판단할 수 있기 때문이다. 한도를 넘으면 비활성 + 사유 표시(BO-983).
                -->
                <b-button
                    v-if="month.downloadable"
                    size="sm"
                    variant="outline-secondary"
                    class="text-nowrap mr-50"
                    :disabled="!canMail(month)"
                    :title="canMail(month) ? '' : $t('v4.Too large to attach')"
                    @click="openMail(job, month)"
                >
                    {{ canMail(month) ? $t('v4.Send by email') : $t('v4.Too large to attach') }}
                </b-button>
                <b-button
                    v-if="month.downloadable"
                    size="sm"
                    variant="outline-primary"
                    class="text-nowrap"
                    :disabled="downloading === `${job.jobId}:${month.month}`"
                    @click="download(job, month)"
                >
                    <b-spinner
                        v-if="downloading === `${job.jobId}:${month.month}`"
                        small
                        class="mr-25"
                    />
                    {{ $t('v4.Download') }}
                </b-button>
                <span
                    v-else
                    class="text-muted"
                >
                    {{ monthStatusText(month) }}
                </span>
            </div>

            <!--
                재생성 중에는 그 자리가 진행 중인 새 요청으로 바뀌어, 이미 만들어 둔 파일에
                접근할 길이 사라진다. 생성이 분~시간이라 그동안 아무것도 못 받게 되므로
                직전 완료분을 같은 줄 아래에 노출한다(BO-983).
            -->
            <template v-if="job.previousJobId && job.previousMonths">
                <div class="px-1 pt-50 small text-muted">
                    <i class="fas fa-history mr-25"></i>
                    {{ $t('v4.Regenerating - the file below is the previous one') }}
                </div>
                <div
                    v-for="prev in downloadablePrevious(job)"
                    :key="`prev-${job.previousJobId}-${prev.month}`"
                    class="px-1 py-50 border-bottom d-flex align-items-center small"
                >
                    <span class="flex-grow-1 text-muted">
                        {{ prev.month }}
                        <span v-if="prev.completedAt">
                            · {{ $t('v4.Created at {time}', { time: shortTime(prev.completedAt) }) }}
                        </span>
                        <span v-if="prev.zipBytes">· {{ formatBytes(prev.zipBytes) }}</span>
                    </span>
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        class="text-nowrap"
                        :disabled="downloading === `${job.previousJobId}:${prev.month}`"
                        @click="download({ jobId: job.previousJobId }, prev)"
                    >
                        <b-spinner
                            v-if="downloading === `${job.previousJobId}:${prev.month}`"
                            small
                            class="mr-25"
                        />
                        {{ $t('v4.Download previous file') }}
                    </b-button>
                </div>
            </template>
        </b-card>

        <b-modal
            v-model="mailOpen"
            :title="$t('v4.Send by email')"
            :ok-title="$t('v4.Send now')"
            :cancel-title="$t('v4.Cancel')"
            :ok-disabled="!mailValid || mailSending"
            :busy="mailSending"
            centered
            @ok.prevent="submitMail"
        >
            <p class="small text-muted mb-50">{{ $t('v4.Enter recipients') }}</p>
            <b-form-group
                v-for="(mail, index) in mailTo"
                :key="`mailto-${index}`"
                class="mb-50"
            >
                <b-input-group>
                    <b-form-input
                        v-model="mailTo[index]"
                        size="sm"
                        type="email"
                        :state="mailStates[index]"
                        :placeholder="$t('v4.Email')"
                    />
                    <b-input-group-append v-if="index > 0">
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            @click="mailTo.splice(index, 1)"
                        >
                            <i class="fas fa-times"></i>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
            <b-button
                size="sm"
                variant="outline-secondary"
                :disabled="mailTo.length >= 3"
                @click="mailTo.push('')"
            >
                + {{ $t('v4.Add Recipient') }}
            </b-button>
        </b-modal>
    </div>
</template>

<script>
import { ref, computed, onUnmounted, getCurrentInstance } from '@vue/composition-api'
import { formatBytes } from '@core/utils/filter'

const STORE_KEY = '$_invoice'
// 요청 폼(BulkDownloadForm)과 같은 규칙을 쓴다. 서버 검증도 동일하다.
const EMAIL_RE = /^[\w!#$%&'*+/=?`{|}~^.-]+@[\w.-]+\.[A-Za-z]{2,}$/
/**
 * 폴링 주기. 작업이 분~시간 단위라 짧게 돌 이유가 없다.
 * 화면을 떠났다 돌아와도 같은 API 로 상태가 복원되므로 놓칠 걱정도 없다.
 */
const POLL_INTERVAL_MS = 10000

export default {
    name: 'BulkJobList',
    setup () {
        const vm = getCurrentInstance().proxy
        const store = vm.$store

        /**
         * 요청이 어느 그룹 대상인지 표시한다(BO-1016).
         *
         * 서버 응답에는 groupIdx(숫자)만 있고 그룹명이 없다. 사용자가 이미 가진 그룹
         * 목록(accountGroups)으로 이름에 매핑한다 — 서버·DDL 변경이 없다. 접근 권한이
         * 사라졌거나 삭제된 그룹은 매핑이 비므로 '그룹 #idx' 로 폴백해 빈칸을 막는다.
         */
        const accountGroups = computed(() => store.getters.accountGroups || [])
        const groupLabel = (job) => {
            const g = accountGroups.value.find(x => String(x.groupIdx) === String(job.groupIdx))
            return g ? g.groupName : `${vm.$t('v4.Group')} #${job.groupIdx}`
        }

        /**
         * 현재 시각. 만료 판정 기준이며 폴링 주기마다 갱신한다.
         *
         * ref 로 두는 이유 — computed 안에서 Date.now() 를 직접 부르면 반응형 의존이 없어
         * 시간이 지나도 재계산되지 않는다. 화면을 켜둔 채 만료 시각을 넘겨도 그대로 남는다.
         */
        const now = ref(Date.now())

        const rawJobs = computed(() => store.getters[`${STORE_KEY}/bulkJobs`] || [])

        /**
         * 만료된 요청은 화면에서 제외한다.
         *
         * 서버도 목록 조회에서 만료 건을 걸러내지만(`findValidJobs`), 그 조회는 화면 진입과
         * 요청 접수 직후에만 일어난다. 폴링은 진행 중인 건의 단건 조회라 만료 건을 치우지 못한다.
         * 화면을 켜둔 채 만료 시각을 넘기면 <b>죽은 다운로드 버튼이 그대로 남는다</b> —
         * 눌러도 presigned URL 이 이미 만료라 실패하고, 사용자는 이유를 알 수 없다.
         *
         * expireAt 은 이미 응답에 있으므로 서버를 다시 부르지 않고 화면에서 바로 걸러낸다.
         */
        const jobs = computed(() => rawJobs.value.filter((job) => {
            if (!job.expireAt) return true
            // 'yyyy-MM-dd HH:mm:ss' — Safari 가 하이픈 형식을 파싱하지 못해 '/' 로 바꾼다.
            const expire = new Date(job.expireAt.replace(/-/g, '/')).getTime()
            return Number.isNaN(expire) || expire > now.value
        }))

        const hasRunning = computed(() =>
            jobs.value.some(job => job.status === 'WAITING' || job.status === 'RUNNING')
        )

        const timer = setInterval(() => {
            // 만료 판정을 다시 돌린다. 진행 중인 건이 없어도 이건 해야 한다.
            now.value = Date.now()

            // 진행 중인 건만 단건 폴링한다. 끝난 건까지 계속 조회하면 서버에 의미 없는 부하가 간다.
            if (!hasRunning.value) return
            jobs.value
                .filter(job => job.status === 'WAITING' || job.status === 'RUNNING')
                .forEach(job => store.dispatch(`${STORE_KEY}/refreshBulkJob`, { jobId: job.jobId }))
        }, POLL_INTERVAL_MS)

        onUnmounted(() => clearInterval(timer))

        const badgeVariant = (status) => ({
            WAITING: 'light-secondary',
            RUNNING: 'light-info',
            DONE: 'light-success',
            PARTIAL: 'light-warning',
            FAILED: 'light-danger'
        }[status] || 'light-secondary')

        const statusText = (job) => {
            if (job.status === 'WAITING' || job.status === 'RUNNING') {
                return vm.$t('v4.Generating - sent as each month completes')
            }
            return vm.$t('v4.{done} of {total} months completed', {
                done: job.doneMonths,
                total: job.totalMonths
            })
        }

        const monthStatusText = (month) => {
            if (month.status === 'DONE') return vm.$t('v4.No issued invoice')
            if (month.status === 'FAILED') return vm.$t('v4.Failed')
            if (month.status === 'RUNNING') return vm.$t('v4.Generating')
            return vm.$t('v4.Waiting')
        }

        /**
         * 다운로드 — 누른 시점에 URL 을 받아 곧바로 이동한다.
         *
         * 목록 응답에 URL 을 담지 않는 이유: presigned URL 은 보유 자체가 권한이라,
         * 미리 받아두면 사용자가 누르지 않아도 전 건의 URL 이 브라우저에 남는다.
         * 받은 URL 은 변수에도 담아두지 않고 즉시 쓰고 버린다(BO-964).
         *
         * 진행 중 표시(downloading)를 두는 이유 — 발급이 왕복 1회라 즉시 반응이 없으면
         * 사용자가 버튼을 연타하고, 그만큼 URL 이 더 발급된다.
         */
        const downloading = ref(null)

        const download = async (job, month) => {
            const key = `${job.jobId}:${month.month}`
            if (downloading.value) return
            downloading.value = key
            try {
                const url = await store.dispatch(`${STORE_KEY}/getBulkDownloadUrl`, {
                    jobId: job.jobId,
                    month: month.month
                })
                if (url) {
                    window.location.assign(url)
                } else {
                    vm.$toast.error(vm.$t('v4.Download link could not be issued'))
                }
            } catch (e) {
                // 만료·권한 등 서버 사유는 응답 메시지에 담겨 온다.
                vm.$toast.error(
                    e?.response?.data?.errorMessage || vm.$t('v4.Download link could not be issued')
                )
                // 만료로 실패했을 수 있으니 상태를 다시 받아 버튼을 정리한다.
                store.dispatch(`${STORE_KEY}/refreshBulkJob`, { jobId: job.jobId })
            } finally {
                downloading.value = null
            }
        }

        /**
         * 같은 조건으로 다시 만든다.
         *
         * 서버가 같은 조건당 최신 1건만 주므로 새 줄이 생기지 않고 이 자리가 갱신된다.
         * 기존 요청 API 를 그대로 쓴다 — 조건은 이 요청이 들고 있는 값 그대로다(BO-983).
         */
        const regenerating = ref(null)

        const regenerate = async (job) => {
            if (regenerating.value) return
            regenerating.value = job.jobId
            try {
                await store.dispatch(`${STORE_KEY}/requestBulkDownload`, {
                    groupIdx: job.groupIdx,
                    payload: {
                        startMonth: job.startMonth,
                        endMonth: job.endMonth,
                        accountIds: job.accountIds,
                        // 발송은 요청 내역에서 따로 고른다. 재생성이 메일을 다시 보내지 않는다.
                        emails: [],
                        lang: job.lang
                    }
                })
                await store.dispatch(`${STORE_KEY}/getBulkJobs`)
            } catch (e) {
                vm.$toast.error(e?.response?.data?.errorMessage || vm.$t('v4.Request failed'))
            } finally {
                regenerating.value = null
            }
        }

        /*
         * 메일 발송 — 완료 후 실제 크기를 보고 사용자가 고른다(BO-983).
         *
         * 첨부 한도는 서버가 요청 정보로 내려준 값을 쓴다. 프론트에 상수로 박으면
         * 서버 설정을 바꿨을 때 버튼만 옛 기준으로 남는다.
         */
        const requestInfo = computed(() => store.getters[`${STORE_KEY}/bulkRequestInfo`] || {})
        const userEmail = computed(() => store.getters.authUserInfo?.email || '')

        const canMail = (month) => {
            const limit = requestInfo.value.mailAttachLimitBytes
            if (!limit || !month.zipBytes) return false
            return month.zipBytes <= limit
        }

        const mailOpen = ref(false)
        const mailTarget = ref(null)
        const mailTo = ref([''])
        const mailSending = ref(false)

        const mailStates = computed(() => mailTo.value.map((v) => {
            const value = (v || '').trim()
            if (!value) return null
            return EMAIL_RE.test(value)
        }))
        const trimmedMailTo = computed(() => mailTo.value.map(v => (v || '').trim()).filter(Boolean))
        const mailValid = computed(() =>
            trimmedMailTo.value.length > 0 && trimmedMailTo.value.every(v => EMAIL_RE.test(v))
        )

        const openMail = (job, month) => {
            mailTarget.value = { jobId: job.jobId, month: month.month }
            // 로그인 계정을 기본값으로 채운다 — 대개 본인이 받는다.
            mailTo.value = [userEmail.value || '']
            mailOpen.value = true
        }

        const submitMail = async () => {
            if (!mailValid.value || mailSending.value) return
            mailSending.value = true
            try {
                await store.dispatch(`${STORE_KEY}/sendBulkMail`, {
                    jobId: mailTarget.value.jobId,
                    month: mailTarget.value.month,
                    emails: trimmedMailTo.value
                })
                mailOpen.value = false
                vm.$toast.success(vm.$t('v4.Mail sent'))
                // 발송 결과(mailResult)를 배지에 반영하려면 다시 받아야 한다.
                store.dispatch(`${STORE_KEY}/getBulkJobs`).catch(() => {})
            } catch (e) {
                vm.$toast.error(e?.response?.data?.errorMessage || vm.$t('v4.Request failed'))
            } finally {
                mailSending.value = false
            }
        }

        /** 직전 완료분 중 실제로 받을 수 있는 월만. 실패한 월까지 보여줄 이유가 없다. */
        const downloadablePrevious = (job) => (job.previousMonths || []).filter(m => m.downloadable)

        /**
         * '2026-07-22 22:55:25' → '07-22 22:55'. 초는 판단에 보탬이 안 된다.
         *
         * 문자열을 그대로 자른다 — Date 로 파싱하면 브라우저 시간대가 한 번 더 적용돼
         * 서버가 이미 KST 로 변환해 보낸 값이 다시 어긋난다. $d 는 이 프로젝트에
         * dateTimeFormats 가 등록돼 있지 않아 쓸 수 없다.
         */
        const shortTime = (value) => {
            if (!value) return ''
            const m = String(value).match(/^\d{4}-(\d{2}-\d{2})[ T](\d{2}:\d{2})/)
            return m ? `${m[1]} ${m[2]}` : String(value)
        }

        /** 수신자는 마스킹해 보여준다. 서버도 마스킹된 값만 내려준다. */
        const maskedRecipients = (job) => {
            const list = job.maskedEmails || []
            if (!list.length) return '-'
            if (list.length === 1) return list[0]
            return vm.$t('v4.{email} and {count} others', {
                email: list[0],
                count: list.length - 1
            })
        }

        return {
            jobs,
            groupLabel,
            formatBytes,
            canMail,
            mailOpen,
            mailTo,
            mailStates,
            mailValid,
            mailSending,
            openMail,
            submitMail,
            regenerating,
            regenerate,
            downloadablePrevious,
            shortTime,
            badgeVariant,
            statusText,
            monthStatusText,
            maskedRecipients,
            downloading,
            download
        }
    }
}
</script>
